import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { resetPassword, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Eye, Loader2, Lock, KeyRound } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError(t("pw_mismatch")); return; }
    if (password.length < 6) { setError(t("pw_min")); return; }
    setLoading(true);
    try {
      await resetPassword(token, password);
      toast.success(t("reset_success"));
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4 py-10" data-testid="reset-password-page">
      <div className="flex items-center gap-2.5 mb-8">
        <span className="w-10 h-10 rounded-md bg-primary text-white flex items-center justify-center">
          <Eye className="w-5 h-5" strokeWidth={2} />
        </span>
        <span className="leading-tight text-left">
          <span className="block text-lg font-bold tracking-tight">VisioScreening</span>
          <span className="block text-[11px] font-medium uppercase tracking-widest text-slate-400">{t("brand_sub")}</span>
        </span>
      </div>

      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="w-11 h-11 rounded-md bg-primary/10 text-primary flex items-center justify-center mb-4">
          <KeyRound className="w-6 h-6" strokeWidth={1.8} />
        </div>
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">{t("reset_title")}</h1>
        <p className="mt-1.5 text-sm text-slate-500">{t("reset_sub")}</p>

        {!token ? (
          <p className="mt-6 text-sm text-destructive" data-testid="reset-no-token">{t("reset_no_token")}</p>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("new_pw_label")}</Label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="reset-password-input" type="password" required minLength={6} className="pl-9" value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder={t("new_pw_ph")} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("confirm_pw_label")}</Label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="reset-confirm-input" type="password" required minLength={6} className="pl-9" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} placeholder={t("confirm_pw_ph")} />
              </div>
            </div>
            {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="reset-submit-btn"
              className="w-full h-11 text-base font-semibold bg-primary hover:bg-primary/90">
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> {t("wait")}</> : t("reset_submit")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
